/**
 * Heart Economy Utilities
 *
 * 하트 이코노미 관련 유틸리티 함수
 * 비용 계산, 상태 판단, 메시지 생성 등을 담당합니다.
 *
 * @author 프론트맨 (Frontend Developer)
 * @based-on 조너선아이브 디자인
 */

import type {
  HeartBalance,
  CardOpenCost,
  CardExtendCost,
  RewardType,
  HeartLevel,
} from '../types/heart';
import {
  HEART_COSTS,
  CARD_EXTENSION_DAYS,
  HEART_LEVEL_THRESHOLD,
  REWARDS,
} from '../types/heart';

// ============================================================================
// Cost Calculations (비용 계산)
// ============================================================================

/**
 * 카드 오픈 비용 계산
 * @param currentBalance 현재 하트 잔액
 * @returns 카드 오픈 비용 정보 (Before/After 포함)
 */
export const calculateCardOpenCost = (
  currentBalance: number
): CardOpenCost => {
  return {
    hearts: HEART_COSTS.CARD_OPEN,
    beforeBalance: currentBalance,
    afterBalance: currentBalance - HEART_COSTS.CARD_OPEN,
  };
};

/**
 * 카드 연장 비용 계산
 * @param currentBalance 현재 하트 잔액
 * @param expiresAt 현재 만료 시간
 * @param currentExtensions 현재 연장 횟수 (기본값: 0)
 * @param extendDeadlineAt 서버 제공 연장 후 만료 시각 (해당 주 일요일 11:00 KST)
 * @returns 카드 연장 비용 정보
 */
export const calculateCardExtendCost = (
  currentBalance: number,
  expiresAt: Date,
  currentExtensions: number = 0,
  extendDeadlineAt?: Date,
  extendDeadlineAtRaw?: string
): CardExtendCost => {
  const extensionDays = CARD_EXTENSION_DAYS;
  // 서버에서 제공하는 extendDeadlineAt(일요일 11:00 KST 고정)을 우선 사용
  const newExpiresAt = extendDeadlineAt
    ?? new Date(expiresAt.getTime() + extensionDays * 24 * 60 * 60 * 1000);

  return {
    hearts: HEART_COSTS.CARD_EXTENSION,
    beforeBalance: currentBalance,
    afterBalance: currentBalance - HEART_COSTS.CARD_EXTENSION,
    expiresAt,
    newExpiresAt,
    newExpiresAtRaw: extendDeadlineAtRaw ?? '',
    extensionDays,
    currentExtensions,
    maxExtensions: 1, // v3 정책: 1회만 연장 가능
  };
};

// ============================================================================
// Status Calculations (상태 계산)
// ============================================================================

/**
 * 하트 잔액 검증
 * @param currentBalance 현재 하트 잔액
 * @param requiredHearts 필요한 하트 개수
 * @returns 하트 잔액 정보 (사용 가능 여부 포함)
 */
export const checkHeartAvailability = (
  currentBalance: number,
  requiredHearts: number
): HeartBalance => {
  return {
    current: currentBalance,
    available: currentBalance >= requiredHearts,
  };
};

/**
 * 하트 상태 레벨 판단
 * @param hearts 현재 하트 개수
 * @returns 하트 상태 레벨 (sufficient/low/critical)
 */
export const getHeartLevel = (hearts: number): HeartLevel => {
  if (hearts >= HEART_LEVEL_THRESHOLD.SUFFICIENT) {
    return 'sufficient';
  }
  if (hearts >= HEART_LEVEL_THRESHOLD.LOW) {
    return 'low';
  }
  return 'critical';
};

// ============================================================================
// Message Generators (메시지 생성)
// ============================================================================

/**
 * 리워드 메시지 생성
 * @param rewardType 리워드 타입
 * @returns 사용자에게 표시할 리워드 메시지
 */
export const getRewardMessage = (rewardType: RewardType): string => {
  const messages: Record<RewardType, string> = {
    signup: `가입을 환영해요! ${REWARDS.SIGNUP}하트가 지급되었습니다`,
    profile: `프로필 완성! ${REWARDS.PROFILE_COMPLETE}하트를 받으셨어요`,
  };
  return messages[rewardType];
};

/**
 * 하트 부족 메시지 생성
 * @param current 현재 하트
 * @param required 필요한 하트
 * @returns 하트 부족 안내 메시지
 */
export const getInsufficientHeartsMessage = (
  current: number,
  required: number
): string => {
  const shortage = required - current;
  if (shortage <= 0) return '';

  return `하트가 ${shortage}개 부족해요. 충전하시겠어요?`;
};

/**
 * 다음 액션 제안 메시지 생성
 * @param rewardType 리워드 타입
 * @returns 다음 액션 제안 메시지 (없으면 null)
 */
export const getNextActionSuggestion = (
  rewardType: RewardType
): string | null => {
  if (rewardType === 'signup') {
    return `프로필을 완성하면 ${REWARDS.PROFILE_COMPLETE}하트를 더 받을 수 있어요!`;
  }
  return null;
};

// ============================================================================
// Validation Helpers (검증 헬퍼)
// ============================================================================

/**
 * 카드 오픈 가능 여부 확인
 * @param currentBalance 현재 하트 잔액
 * @returns 카드 오픈 가능 여부
 */
export const canOpenCard = (currentBalance: number): boolean => {
  return currentBalance >= HEART_COSTS.CARD_OPEN;
};

/**
 * 카드 연장 가능 여부 확인
 * @param currentBalance 현재 하트 잔액
 * @returns 카드 연장 가능 여부
 */
export const canExtendCard = (currentBalance: number): boolean => {
  return currentBalance >= HEART_COSTS.CARD_EXTENSION;
};
